import type { ComponentType, ReactNode } from 'react';
import type { Id, ToastOptions } from 'react-toastify';
import { getIsTrainIncident } from '@rsos/base-utils';
import AlertToast from '@rsos/components/capstone/AlertToast/AlertToast';
import ConversationsToast from '@rsos/components/capstone/ConversationsToast';
import InboundReplyToast, {
  InboundReplyVariant,
} from '@rsos/components/capstone/InboundReplyToast/InboundReplyToast';
import InteragencyChatToast from '@rsos/components/capstone/InteragencyChatToast/InteragencyChatToast';
import {
  displayToast,
  dismissToast,
  dismissAll,
  updateToast,
  isToastActive,
} from '@rsos/components/capstone/base/Toast';
import {
  toastMessageSuccess,
  toastMessageError,
  toastMessageInfo,
  toastMessageDefault,
} from '@rsos/components/capstone/base/Toast/ToastComponents';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertEmergencyTypes';
import { conversationStateValues } from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';

export const THIRTY_SECONDS = 1000 * 30;

export interface NotificationData {
  options?: ToastOptions;
  message?: ReactNode;
  customIcon?: ComponentType;
  noIcon?: boolean;
}

interface AlertNotificationData {
  alert?: { emergency_type?: { name?: string } };
  options?: ToastOptions;
}

interface ConversationNotificationData {
  conversation: conversationStateValues;
  handleButtonClick?: () => void;
  options?: ToastOptions;
}

interface InboundReplyNotificationData {
  callerID: string;
  variant: InboundReplyVariant;
  onOpen: () => void;
  options?: ToastOptions;
}

interface InteragencyMessageNotificationData {
  conversationName: string | null;
  messagePreview: string;
  onOpen: () => void;
  options?: ToastOptions;
}

export const notifySuccess = (data: string | NotificationData): Id => {
  const defaultOptions = {
    closeButton: false,
  };
  const toastOptions =
    typeof data === 'object'
      ? { ...defaultOptions, ...data?.options }
      : defaultOptions;
  const message = typeof data === 'object' ? data?.message : data;
  const noIcon = typeof data === 'object' ? data?.noIcon : undefined;
  const customIcon = typeof data === 'object' ? data?.customIcon : undefined;

  const toastParams = {
    message: toastMessageSuccess(message, noIcon, customIcon),
    options: toastOptions,
    className: 'Toastify__overwrite-success',
  };

  return displayToast(toastParams);
};

export const notifyError = (
  data: string | NotificationData,
  opts: ToastOptions = {},
): void => {
  const defaultOptions = {
    autoClose: THIRTY_SECONDS,
    ...opts,
  };
  const toastOptions =
    typeof data === 'object'
      ? { ...defaultOptions, ...data?.options }
      : defaultOptions;
  const message = typeof data === 'object' ? data?.message : data;
  const noIcon = typeof data === 'object' ? data?.noIcon : undefined;
  const customIcon = typeof data === 'object' ? data?.customIcon : undefined;

  const toastParams = {
    message: toastMessageError(message, noIcon, customIcon),
    options: toastOptions,
    className: 'Toastify__overwrite-error',
  };

  displayToast(toastParams);
};

export const notifyDefault = (data: string | NotificationData): void => {
  const defaultOptions = {
    closeButton: false,
  };
  const toastOptions =
    typeof data === 'object'
      ? { ...defaultOptions, ...data?.options }
      : defaultOptions;
  const message = typeof data === 'object' ? data?.message : data;
  const noIcon = typeof data === 'object' ? data?.noIcon : undefined;
  const customIcon = typeof data === 'object' ? data?.customIcon : undefined;

  const toastParams = {
    message: toastMessageDefault(message, noIcon, customIcon),
    options: toastOptions,
    className: 'Toastify__overwrite-default',
  };

  displayToast(toastParams);
};

export const notifyInfo = (data: string | NotificationData): void => {
  const message = typeof data === 'object' ? data?.message : data;
  const options = typeof data === 'object' ? data?.options : undefined;
  const noIcon = typeof data === 'object' ? data?.noIcon : undefined;
  const customIcon = typeof data === 'object' ? data?.customIcon : undefined;

  const toastParams = {
    message: toastMessageInfo(message, noIcon, customIcon),
    options,
    className: 'Toastify__overwrite-info',
  };

  displayToast(toastParams);
};

export const notifyGenericAlert = (data: AlertNotificationData): Id => {
  const defaultOptions = {
    closeButton: true,
    autoClose: THIRTY_SECONDS,
    closeOnClick: false,
  };
  const toastOptions = { ...defaultOptions, ...data?.options };
  const toastParams = {
    message: <AlertToast alert={data?.alert} />,
    options: toastOptions,
    className: getToastCssClass(data?.alert?.emergency_type?.name),
  };

  return displayToast(toastParams);
};

export const notifyConversation = (data: ConversationNotificationData): Id => {
  const defaultOptions = {
    closeButton: true,
    autoClose: THIRTY_SECONDS,
    closeOnClick: false,
  };
  const toastOptions = { ...defaultOptions, ...data?.options };
  const toastParams = {
    message: (
      <ConversationsToast
        conversation={data?.conversation}
        handleButtonClick={data?.handleButtonClick}
      />
    ),
    options: toastOptions,
    className: 'Toastify__conversations-default',
  };

  return displayToast(toastParams);
};

/**
 * Replaces rather than stacks when a banner with the same `toastId` is up.
 * react-toastify silently ignores a plain toast() call in that case.
 */
const displayOrReplaceConversationToast = (
  message: ReactNode,
  options?: ToastOptions,
): Id => {
  const toastOptions = {
    closeButton: true,
    autoClose: THIRTY_SECONDS,
    closeOnClick: false,
    ...options,
  };

  if (toastOptions.toastId && isToastActive(toastOptions.toastId)) {
    updateToast(toastOptions.toastId, {
      render: message,
      autoClose: toastOptions.autoClose,
    });
    return toastOptions.toastId;
  }

  return displayToast({
    message,
    options: toastOptions,
    className: 'Toastify__conversations-default',
  });
};

export const notifyInteragencyMessage = (
  data: InteragencyMessageNotificationData,
): Id => {
  const { conversationName, messagePreview, onOpen, options } = data;
  return displayOrReplaceConversationToast(
    <InteragencyChatToast
      conversationName={conversationName}
      messagePreview={messagePreview}
      onOpen={onOpen}
    />,
    options,
  );
};

export const notifyInboundReply = (data: InboundReplyNotificationData): Id => {
  const { callerID, variant, onOpen, options } = data;
  return displayOrReplaceConversationToast(
    <InboundReplyToast callerID={callerID} variant={variant} onOpen={onOpen} />,
    options,
  );
};

function getToastCssClass(emergencyType?: string): string {
  const isTrainIncident = getIsTrainIncident(emergencyType, true);
  if (emergencyType === ACTIVE_ASSAILANT) {
    return 'Toastify__active-assailant';
  } else if (isTrainIncident) {
    return 'Toastify__train-incident';
  }
  return 'Toastify__alert-default';
}

export const dismissToastByID = (toastId: Id): void => {
  dismissToast(toastId);
};

export const dismissAllToasts = (): void => {
  dismissAll();
};
